// Vendor Program v2 PR 2 — compact per-loan "Monthly reports" card on the
// loan Vendors tab: outstanding (expected-vs-filed) chip + the loan's
// recent reports, reusing the shared review drawer (deep-linkable via
// ?tab=vendors&report=<id>). Self-gates on Vendors.Read (LoanVfiSummaryCard
// precedent) so loan roles without the vendor grant see nothing broken.

import { formatPeriodMonth } from "@/design-system/lib/format";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, StatusBadge } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  useVendorReportCompliance,
  useVendorReports,
} from "@/hooks/portfolioAdmin/useVendorReports";
import { fmtMoney } from "@/components/portfolioAdmin/vendors/billing/vendorBillingFormat";
import { VendorReportReviewDrawer } from "@/components/portfolioAdmin/vendors/reports/VendorReportReviewDrawer";
import {
  VENDOR_REPORT_STATUS_LABEL,
  vendorReportComplianceBadge,
  vendorReportStatusTone,
} from "@/components/portfolioAdmin/vendors/reports/vendorReportFormat";

const RECENT_COUNT = 5;

export function LoanVendorReportsCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const openReportId = params.get("report");

  const canRead = auth.hasPermission("Vendors.Read");
  const reportsQuery = useVendorReports({ loanId }, { enabled: canRead });
  const complianceQuery = useVendorReportCompliance(
    { loanId },
    { enabled: canRead },
  );

  const recent = useMemo(
    () =>
      [...(reportsQuery.data ?? [])]
        .sort((a, b) => b.periodStart.localeCompare(a.periodStart))
        .slice(0, RECENT_COUNT),
    [reportsQuery.data],
  );

  if (!canRead) return null;

  // Period boundaries are wall-calendar months stored at UTC midnight —
  // rendering them through the tz-aware formatter shifted June into May.
  const month = (iso: string) => formatPeriodMonth(iso, "short");
  const compliance = complianceQuery.data;
  const badge = compliance ? vendorReportComplianceBadge(compliance) : null;

  const setReportParam = (value: string | null) => {
    const updated = new URLSearchParams(params);
    if (value) updated.set("report", value);
    else updated.delete("report");
    setParams(updated, { replace: true });
  };

  return (
    <Card data-testid="loan-vendor-reports-card">
      <div className="flex items-center justify-between gap-space-2 flex-wrap mb-space-2">
        <h3 className="font-display font-bold text-h3 m-0">
          Monthly reports (this loan)
        </h3>
        {badge && (
          <StatusBadge
            tone={badge.tone}
            hint={badge.hint}
            data-testid="loan-vendor-reports-outstanding-chip"
          >
            {badge.label}
          </StatusBadge>
        )}
      </div>

      {reportsQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">
          Loading vendor reports…
        </p>
      )}
      {reportsQuery.isError && (
        <p className="text-small text-status-error m-0">
          Could not load vendor reports. Refresh to retry.
        </p>
      )}
      {reportsQuery.isSuccess &&
        (recent.length === 0 ? (
          <p className="text-small text-gray-mid m-0">
            No monthly vendor reports on this loan yet. Vendors file them from
            their portal each month.
          </p>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
            {recent.map((report) => (
              <li key={report.id}>
                <button
                  type="button"
                  onClick={() => setReportParam(report.id)}
                  className="flex w-full items-center justify-between gap-space-2 border-b border-border-light pb-space-1 text-small text-left hover:bg-white-warm transition-colors"
                  data-testid={`loan-vendor-report-row-${report.id}`}
                >
                  <span className="flex items-center gap-space-2 min-w-0">
                    <span className="whitespace-nowrap font-medium">
                      {month(report.periodStart)}
                    </span>
                    <span className="text-gray-mid truncate">
                      {report.vendorName} · {report.roleOnLoan}
                    </span>
                    <StatusBadge tone={vendorReportStatusTone(report.status)}>
                      {VENDOR_REPORT_STATUS_LABEL[report.status]}
                    </StatusBadge>
                  </span>
                  <span className="text-right tabular-nums whitespace-nowrap">
                    {report.selfReportedInvoiceAmount != null
                      ? fmtMoney(report.selfReportedInvoiceAmount)
                      : "—"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ))}

      {openReportId && (
        <VendorReportReviewDrawer
          reportId={openReportId}
          open={!!openReportId}
          onClose={() => setReportParam(null)}
        />
      )}
    </Card>
  );
}
